import { useState, useCallback, useEffect, useContext, createContext } from 'react';
import { ThemeProvider } from 'styled-components';

import useSessionStorage from '@vimeo/core/hooks/use-storage/useClientStorage';
import { NetworkError } from '@vimeo/gctl-api/fetchers/_helpers';
import { usePostGuestUsers } from '@vimeo/gctl-api/hooks/useGuestUsers';
import { translate } from '@vimeo/i18n-runtime';
import { themes } from '@vimeo/iris/themes';
import ReviewAsGuestModal from '@vimeo/ui/core/ReviewAsGuestModal';
import InlineReviewAsGuestPrompt from '@vimeo/ui/core/ReviewAsGuestModal/InlineReviewAsGuestPrompt';

import { CommentsContainerPropsType } from '../CommentsCommon/CommentsContainer';
import { DismissConfirmationModal } from '../CommentsCommon/DismissConfirmationModal';
import { STASHED_GUEST_USER_KEY } from '../utils/constants';
import { getStorageInstance } from '../utils/inMemoryStorage';
import { useVideoPrivacy } from './comments/use-video-privacy';
import { LoginModalProvider, LoginModal } from './use-login-modal';
import { useUserPermissions } from './use-user-permission';
import { useViewer } from './use-viewer';

const noop = () => new Error('Function is not initialized');

interface GuestLoginModalState {
  xsrft: string;
  isActive: boolean;
  isGuestCommentWithEmail?: boolean;
  isSubmitLoading?: boolean;
  emailErrorCode?: number | null;
  titleOverride?: string;
  submitButtonOverride?: string;
}

interface GuestLoginModalContext {
  modalState: GuestLoginModalState;
  updateModalState: (modalState: Partial<GuestLoginModalState>) => void;
  toggleGuestLoginModal: (
    shouldShow: boolean,
    options?: { titleOverride?: string; submitButtonOverride?: string },
  ) => void;
  guestUser?: string;
  setStashedGuestUser: (stashedGuestUser: string) => void;
}
const defaultModalState: GuestLoginModalState = {
  xsrft: '',
  isActive: false,
  isGuestCommentWithEmail: true,
  isSubmitLoading: false,
  emailErrorCode: null,
};

export const guestLoginModalContext = createContext<GuestLoginModalContext>({
  modalState: defaultModalState,
  updateModalState: noop,
  toggleGuestLoginModal: noop,
  setStashedGuestUser: noop,
});

export const GuestLoginModalProvider = ({ children }: { children: React.ReactNode }) => {
  const [modalState, setModalState] = useState<GuestLoginModalState>(defaultModalState);
  const viewer = useViewer();
  const expiryTtlTime = 0.25; // 6 hours for token expiry
  const storage = getStorageInstance();
  const { value: guestUser, set: setStashedGuestUser } = useSessionStorage(
    STASHED_GUEST_USER_KEY,
    null,
    expiryTtlTime,
    storage,
  );

  const updateModalState = (event: Partial<GuestLoginModalState>) => {
    setModalState((prev) => ({ ...prev, ...event }));
  };

  const toggleGuestLoginModal = (
    event: boolean,
    options?: { titleOverride?: string; submitButtonOverride?: string },
  ) => {
    setModalState((state) => ({
      ...state,
      isActive: event,
      titleOverride: event ? options?.titleOverride : undefined,
      submitButtonOverride: event ? options?.submitButtonOverride : undefined,
    }));
  };

  useEffect(() => {
    if (!viewer) return;

    if (!modalState.xsrft) {
      updateModalState({ xsrft: viewer.xsrft });
    }
  }, [modalState, viewer]);

  return (
    <guestLoginModalContext.Provider
      value={{
        modalState,
        updateModalState,
        toggleGuestLoginModal,
        guestUser,
        setStashedGuestUser,
      }}
    >
      {children}
    </guestLoginModalContext.Provider>
  );
};

type GuestLoginModalProps = {
  isInlinePrompt?: boolean;
  canReactToCollabComments?: boolean;
};

export const GuestLoginModal = ({
  isInlinePrompt = false,
  canReactToCollabComments,
}: GuestLoginModalProps) => {
  const { modalState, updateModalState, guestUser, setStashedGuestUser } =
    useContext(guestLoginModalContext);
  const [createGuestUser, { loading: createGuestUserLoading, error, data: createdGuestUser }] =
    usePostGuestUsers();
  const [isDismissConfirmationActive, setDismissConfirmationActive] = useState(false);

  const submitCommentAsGuest = useCallback(
    async (name: string) => {
      await createGuestUser({
        select: ['uri', 'name', 'metadata.interactions.comments'],
        variables: { name },
      });
      window.location.reload();
    },
    [createGuestUser],
  );

  const onCommentSignup = useCallback(() => {
    updateModalState(defaultModalState);
    window.location.reload();
  }, [updateModalState]);

  const openDismissConfirmation = useCallback(() => {
    setDismissConfirmationActive(true);
  }, []);

  const onCloseModal = useCallback(() => {
    updateModalState({ isActive: false });
  }, [updateModalState]);

  const onContinueAddingName = useCallback(() => {
    setDismissConfirmationActive(false);
  }, []);

  const onGoBackToDraft = useCallback(() => {
    setDismissConfirmationActive(false);
    updateModalState({ isActive: false, emailErrorCode: null });
  }, [updateModalState]);

  const resetEmailErrorCode = useCallback(
    (errorCode: number) => {
      updateModalState({ emailErrorCode: errorCode });
    },
    [updateModalState],
  );

  useEffect(() => {
    const saveGuestInfoToSession = async () => {
      if (modalState.isSubmitLoading !== createGuestUserLoading) {
        updateModalState({ isSubmitLoading: createGuestUserLoading });
      }

      if (!createGuestUserLoading && !error && createdGuestUser && !guestUser) {
        //set guest user id,jwt to session
        const guestUserName = createdGuestUser.name;
        const guestUserId = createdGuestUser.uri.split('/').pop();
        const guestUserJwt = createdGuestUser.metadata.interactions.comments;
        setStashedGuestUser(JSON.stringify({ guestUserName, guestUserId, guestUserJwt }));
        updateModalState({ isActive: false });
      } else if (error instanceof NetworkError && !error.res.bodyUsed) {
        const response = await error.res.json();
        updateModalState({ emailErrorCode: response?.error_code as number });
      }
    };

    saveGuestInfoToSession();
  }, [
    modalState,
    createGuestUserLoading,
    error,
    createdGuestUser,
    updateModalState,
    setStashedGuestUser,
    guestUser,
  ]);

  useEffect(() => {
    if (!modalState.isActive) {
      setDismissConfirmationActive(false);
    }
  }, [modalState.isActive]);

  if (isInlinePrompt) {
    return (
      <ThemeProvider theme={themes.light}>
        <InlineReviewAsGuestPrompt
          xsrft={modalState.xsrft}
          isActive={modalState.isActive && !isDismissConfirmationActive}
          onDismiss={openDismissConfirmation}
          onSuccessfulLogin={onCommentSignup}
          submitCommentAsGuest={submitCommentAsGuest}
          isSubmitLoading={modalState.isSubmitLoading}
          apiErrorCode={modalState.emailErrorCode}
          resetApiErrorCode={resetEmailErrorCode}
          titleText={modalState.titleOverride ?? translate('Add your name to comment')}
          submitButtonText={modalState.submitButtonOverride}
          namePlaceholderText={translate('Your full name')}
          canReactToCollabComments={canReactToCollabComments}
          isCompact
        />
        <DismissConfirmationModal
          isOpen={isDismissConfirmationActive}
          onContinue={onContinueAddingName}
          onGoBack={onGoBackToDraft}
          canReactToCollabComments={canReactToCollabComments}
        />
      </ThemeProvider>
    );
  }

  return (
    // the above themeprovider can be removed once , login modal is migrated to bokeh.
    // the above provider prevents dark mode on input in login modal
    <ThemeProvider theme={themes.light}>
      <ReviewAsGuestModal
        {...modalState}
        closeModal={onCloseModal}
        onSuccessfulLogin={onCommentSignup}
        submitCommentAsGuest={submitCommentAsGuest}
        resetApiErrorCode={resetEmailErrorCode}
        titleText={modalState.titleOverride}
        submitButtonText={modalState.submitButtonOverride}
        canReactToCollabComments={canReactToCollabComments}
      />
    </ThemeProvider>
  );
};

export const withGuestLoginModal = (
  CommentsContainerComponent: React.FC<CommentsContainerPropsType>,
) => {
  return (props: CommentsContainerPropsType) => {
    const { privacy } = useVideoPrivacy(props.clipRequestId, props.showcaseId, props.reviewId);
    const { canReactToCollabComments } = useUserPermissions(
      props.clipRequestId,
      props.showcaseId,
      props.reviewId,
    );
    const shouldUseGuestModal = privacy?.view === 'unlisted' || !!props.reviewId;
    return (
      <GuestLoginModalProvider>
        <LoginModalProvider>
          <CommentsContainerComponent {...props} shouldUseGuestModal={shouldUseGuestModal} />
          {!props.accountlessCommentRedesignEnabled && shouldUseGuestModal && (
            <GuestLoginModal canReactToCollabComments={canReactToCollabComments} />
          )}
          {!shouldUseGuestModal && <LoginModal />}
        </LoginModalProvider>
      </GuestLoginModalProvider>
    );
  };
};
